/**
 * Precision Responsible AI, the COM028 sections of a use case record.
 *
 * The policy (Responsible Use of Artificial Intelligence, COM028) names
 * what must be on the record before the committee decides. These pieces
 * put each of them on the screen in plain form:
 *
 *   ReadinessPanel      what is in place and what is owed before approval,
 *                       the review path (raised by the officer when needed),
 *                       the Business Owner, and the Registration Only
 *                       confirmation (Sec 2, 7)
 *   AssessmentView      the impact assessment with every Sec 4 element,
 *                       editable in place, with its completeness
 *   SecurityReviewTab   the Sec 5 security review
 *   TestingTab          the Sec 6 pre implementation test records
 *   ContingencyEditor   contingencies with an owner and a due date (Sec 7)
 *   ProgramSettingsCard monitoring frequency per rating and the Material
 *                       Change definition, set by the Compliance Officer
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const INPUT = 'w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300';
const LABEL = 'text-xs font-semibold text-slate-500';
const BTN = 'px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50';
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; } catch { return '—'; } };

export const TIER_LABELS = { full_review: 'Full review', fast_track: 'Expedited review', register_only: 'Registration only' };
const TIER_ORDER = ['register_only', 'fast_track', 'full_review'];

async function send(url, method, body) {
  const r = await fetch(url, { method, credentials: 'include', headers: body instanceof FormData ? undefined : { 'Content-Type': 'application/json' }, body: body instanceof FormData ? body : JSON.stringify(body || {}) });
  const d = await r.json().catch(() => ({ success: false, message: 'Server error' }));
  if (!d.success) throw new Error(d.message || 'Request failed');
  return d;
}

/** The organization's users, for the Business Owner picker. */
export function useOrgUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    let on = true;
    fetch(`${API}/api/ai-command/users`, { credentials: 'include' }).then((r) => r.json())
      .then((d) => { if (on && d.success) setUsers(d.users || []); }).catch(() => {});
    return () => { on = false; };
  }, []);
  return users;
}

/**
 * Business Owner: a user of the organization, or a name typed in when the
 * owner is not a user yet. The policy wants an accountable manager on
 * every tool; the user link is what lets them see it.
 */
export function OwnerPicker({ users, ownerUserId, ownerLabel, onChange }) {
  const [typing, setTyping] = useState(!ownerUserId && !!ownerLabel);
  return (
    <div>
      <label className={LABEL}>Business owner</label>
      {!typing ? (
        <select className={INPUT} value={ownerUserId || ''} onChange={(e) => {
          if (e.target.value === '__other') { setTyping(true); onChange({ owner_user_id: null, owner_label: ownerLabel || '' }); return; }
          const u = users.find((x) => x.id === e.target.value);
          onChange({ owner_user_id: u ? u.id : null, owner_label: u ? (u.name || u.email) : '' });
        }}>
          <option value="">Choose the accountable manager…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name ? `${u.name} · ${u.email}` : u.email}</option>)}
          <option value="__other">Not a user yet, type a name</option>
        </select>
      ) : (
        <div className="flex gap-2">
          <input className={INPUT} value={ownerLabel || ''} placeholder="Name and title" onChange={(e) => onChange({ owner_user_id: null, owner_label: e.target.value })} />
          <button type="button" onClick={() => setTyping(false)} className="text-xs text-slate-500 whitespace-nowrap hover:text-slate-700">Pick a user</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
export function ReadinessPanel({ d, ucId, compliance, onChanged }) {
  const uc = d.use_case;
  const r = d.readiness || { tier: uc.risk_tier, ready: false, items: [] };
  const users = useOrgUsers();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [editOwner, setEditOwner] = useState(false);
  const [owner, setOwner] = useState({ owner_user_id: uc.owner_user_id || null, owner_label: uc.owner_label || '' });
  const derived = d.derived_tier || uc.risk_tier;
  const raiseOptions = TIER_ORDER.filter((t) => TIER_ORDER.indexOf(t) > TIER_ORDER.indexOf(derived));
  const decided = ['approved', 'approved_with_contingencies', 'declined', 'retired', 'registered'].includes(uc.status);

  const patch = async (body, what) => {
    setBusy(what); setErr('');
    try { await send(`${API}/api/ai-command/use-cases/${ucId}`, 'PATCH', body); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const confirm = async () => {
    setBusy('confirm'); setErr('');
    try { await send(`${API}/api/ai-command/use-cases/${ucId}/confirm-registration`, 'POST', {}); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-1">Review path</div>
          <div className="text-sm text-slate-800">
            <b>{TIER_LABELS[r.tier] || r.tier}</b>
            {uc.tier_override && uc.tier_override === r.tier && r.tier !== derived && (
              <span className="text-xs text-amber-700 ml-2">raised by the Compliance Officer from {TIER_LABELS[derived]}</span>
            )}
          </div>
          {compliance && !decided && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <select className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs" value={uc.tier_override || ''} disabled={busy === 'tier'}
                onChange={(e) => patch({ tier_override: e.target.value || null }, 'tier')}>
                <option value="">As screened: {TIER_LABELS[derived]}</option>
                {raiseOptions.map((t) => <option key={t} value={t}>Raise to {TIER_LABELS[t]}</option>)}
              </select>
              <span className="text-[11px] text-slate-400">The officer may assign a more stringent path at any time, never a less stringent one.</span>
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-1">Business owner</div>
          {!editOwner ? (
            <div className="text-sm text-slate-800 flex items-center gap-2 flex-wrap">
              <span>{uc.owner_label || <span className="text-rose-600">Not named</span>}</span>
              {uc.owner_user_id ? <span className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">sees this tool</span>
                : uc.owner_label ? <span className="text-[11px] text-slate-500">name only, not a user</span> : null}
              {compliance && <button onClick={() => setEditOwner(true)} className="text-xs text-orange-700 font-semibold hover:underline">Change</button>}
            </div>
          ) : (
            <div className="space-y-2">
              <OwnerPicker users={users} ownerUserId={owner.owner_user_id} ownerLabel={owner.owner_label} onChange={setOwner} />
              <div className="flex gap-2">
                <button onClick={async () => { await patch(owner, 'owner'); setEditOwner(false); }} disabled={busy === 'owner'} className={`${BTN} bg-slate-900 text-white`}>Save</button>
                <button onClick={() => setEditOwner(false)} className={`${BTN} border border-slate-200 text-slate-600`}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {r.tier === 'register_only' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-slate-700">
            {uc.status === 'registered'
              ? <>Registered and confirmed {fmtDate(uc.registration_confirmed_at)}. Recorded in the AI Inventory with its Business Owner; no committee decision is required.</>
              : <>A Registration Only tool is recorded with its Business Owner and confirmed by Compliance before use. No assessment or committee decision is required unless the officer raises the path.</>}
          </div>
          {compliance && !decided && (
            <button onClick={confirm} disabled={busy === 'confirm'} className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-700`}>
              {busy === 'confirm' ? 'Confirming…' : 'Confirm registration'}
            </button>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-bold tracking-wide text-slate-400 uppercase">Before the committee can approve</div>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.ready ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{r.ready ? 'READY' : `${r.items.filter((i) => !i.met).length} OWED`}</span>
          </div>
          <ul className="space-y-1.5">
            {r.items.map((it) => (
              <li key={it.key} className="flex items-start gap-2 text-sm">
                <span className={`mt-0.5 shrink-0 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center ${it.met ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{it.met ? '✓' : '!'}</span>
                <span className="text-slate-800"><b>{it.label}.</b> <span className="text-slate-600">{it.note}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {err && <p className="text-sm text-rose-600">{err}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
const ASSESSMENT_FIELDS = [
  ['intended_use', 'Intended purpose'],
  ['benefits', 'Expected benefits'],
  ['tool_type', 'Type of tool', 'select', [['rules_based', 'Rules based'], ['machine_learning', 'Machine learning'], ['generative', 'Generative AI'], ['hybrid', 'Hybrid']]],
  ['autonomy_explainability', 'Degree of autonomy and how far outputs can be explained'],
  ['harms', 'Foreseeable risks and who could be harmed'],
  ['affected_populations', 'Members, providers and staff affected'],
  ['data_inputs', 'Data used, including protected characteristics or proxies'],
  ['human_oversight', 'Human oversight: review points, extent, who, override authority, how review is verified'],
  ['transparency', 'Required disclosures and how they are made'],
  ['security_privacy', 'Security and privacy safeguards'],
  ['bias_testing', 'Bias and fairness testing: testing, population, acceptance criteria'],
  ['performance_metrics', 'Performance metrics with measurable targets'],
  ['mitigation_plan', 'Mitigation plan: each risk, its control, its owner'],
  ['backup_plan', 'Backup Plan'],
  ['vendor_joint_monitoring', 'Joint monitoring with the vendor', 'vendor'],
  ['monitoring_plan', 'Monitoring plan: measures, frequency, thresholds, who'],
  ['regulatory_notes', 'Applicable Medicare, Medicaid, privacy and state requirements'],
];

export function AssessmentView({ d, ucId, compliance, onChanged }) {
  const uc = d.use_case;
  const current = d.assessments.find((a) => a.is_current) || d.assessments[0] || null;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState({});
  useEffect(() => { if (current) setF({ ...current }); }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!current) return <p className="text-sm text-slate-400">No assessment yet. {compliance ? 'Draft one to open the committee path, then complete every element.' : 'The compliance office prepares this with the Business Owner.'}</p>;

  const c = d.assessment_completeness || { complete: false, missing: [] };
  const need = d.required_rating;
  const fields = ASSESSMENT_FIELDS.filter(([, , kind]) => kind !== 'vendor' || uc.kind === 'vendor_tool');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const body = {};
      for (const [k] of fields) body[k] = f[k] ?? '';
      body.risk_rating = need || f.risk_rating || null;
      body.recommendation = f.recommendation || null;
      body.narrative = f.narrative ?? '';
      await send(`${API}/api/ai-command/use-cases/${ucId}/assessments/${current.id}`, 'PATCH', body);
      setEditing(false); onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
        Version {current.version_number} · {fmtDate(current.created_at)}{current.generated_by_ai ? ' · drafted, not yet edited' : ' · edited by the compliance office'}
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${c.complete ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{c.complete ? 'COMPLETE' : 'PENDING FURTHER REVIEW'}</span>
        {compliance && !editing && <button onClick={() => setEditing(true)} className="ml-auto text-xs font-semibold text-orange-700 hover:underline">Edit assessment</button>}
      </div>
      {!c.complete && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          An assessment with any incomplete element may not be presented to the Committee as complete. Still owed: {c.missing.join('; ')}.
        </div>
      )}

      {editing ? (
        <div className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className={LABEL}>Overall risk rating</label>
              <select className={INPUT} value={need || f.risk_rating || ''} disabled={!!need} onChange={(e) => setF({ ...f, risk_rating: e.target.value })}>
                <option value="">Choose…</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
              {need && <p className="text-[11px] text-amber-700 mt-1">Rated High under Section 4: this tool may affect UM or coverage, or acts without human approval of each output.</p>}
            </div>
            <div>
              <label className={LABEL}>Recommendation to the Committee</label>
              <select className={INPUT} value={f.recommendation || ''} onChange={(e) => setF({ ...f, recommendation: e.target.value })}>
                <option value="">Choose…</option>
                <option value="proceed">Approve</option><option value="proceed_with_contingencies">Approve with contingencies</option>
                <option value="needs_information">Return for further review</option><option value="do_not_proceed">Decline</option>
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL}>Summary for the Committee</label>
            <textarea className={INPUT} rows={3} value={f.narrative || ''} onChange={(e) => setF({ ...f, narrative: e.target.value })} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {fields.map(([k, label, kind, options]) => (
              <div key={k} className={kind === 'select' ? '' : 'md:col-span-2'}>
                <label className={LABEL}>{label}</label>
                {kind === 'select' ? (
                  <select className={INPUT} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })}>
                    <option value="">Choose…</option>{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ) : (
                  <textarea className={INPUT} rows={3} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
                )}
              </div>
            ))}
          </div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setEditing(false); setF({ ...current }); }} className={`${BTN} border border-slate-200 text-slate-600`}>Cancel</button>
            <button onClick={save} disabled={busy} className={`${BTN} bg-slate-900 text-white`}>{busy ? 'Saving…' : 'Save assessment'}</button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {current.risk_rating && <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">Overall risk: {current.risk_rating}</span>}
            {current.recommendation && <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 ring-1 ring-orange-200">{current.recommendation.replace(/_/g, ' ')}</span>}
            {current.tool_type && <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">{current.tool_type.replace(/_/g, ' ')}</span>}
          </div>
          {current.narrative && <p className="text-sm text-slate-800 whitespace-pre-wrap border-l-2 border-orange-200 pl-3">{current.narrative}</p>}
          <div className="grid md:grid-cols-2 gap-4">
            {fields.filter(([, , kind]) => kind !== 'select').map(([k, label]) => {
              const v = current[k];
              const long = String(v || '').length > 260;
              return (
                <div key={k}>
                  <div className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-1">{label}</div>
                  {v ? (
                    <>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{open[k] || !long ? v : `${String(v).slice(0, 240)}…`}</p>
                      {long && <button onClick={() => setOpen({ ...open, [k]: !open[k] })} className="text-xs font-semibold text-orange-600 hover:text-orange-800 mt-1">{open[k] ? 'Show less' : 'Show more'}</button>}
                    </>
                  ) : <p className="text-sm text-amber-700">Not yet documented.</p>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
export function SecurityReviewTab({ d, ucId, compliance, onChanged }) {
  const uc = d.use_case;
  const sr = d.security_review;
  const [f, setF] = useState(sr || {});
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => { setF(sr || {}); }, [sr?.id, sr?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (status) => {
    setBusy(status); setErr('');
    try { await send(`${API}/api/ai-command/use-cases/${ucId}/security-review`, 'PUT', { ...f, status }); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const yesNo = (k, label) => (
    <div key={k}>
      <label className={LABEL}>{label}</label>
      <div className="flex gap-2 mt-1">
        {[[true, 'Yes'], [false, 'No']].map(([v, l]) => (
          <button key={l} type="button" disabled={!compliance} onClick={() => setF({ ...f, [k]: v })}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${f[k] === v ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-500'}`}>{l}</button>
        ))}
      </div>
    </div>
  );
  const text = (k, label, rows = 2) => (
    <div key={k}>
      <label className={LABEL}>{label}</label>
      <textarea className={INPUT} rows={rows} value={f[k] || ''} readOnly={!compliance} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
        {sr?.status === 'complete'
          ? <><span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">COMPLETE</span> {fmtDate(sr.reviewed_at)} · {sr.reviewed_by_label}</>
          : <><span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">PENDING</span> The information security member of the Committee, or a designee, conducts this review before the Committee decides.</>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {text('user_access', 'User access and permissions: who may use the tool and what each user may view')}
        {text('data_location', 'Where data is stored and processed')}
        {text('retention', 'How long data is retained')}
        {text('logging', 'Logging of tool use and outputs, enough to investigate an issue')}
        {yesNo('offshore', 'Is data stored, processed or accessed outside the United States?')}
        {yesNo('trains_on_org_data', 'May our data be used to train or improve the tool?')}
        {text('training_notes', 'Notes on training use, if any')}
        {uc.kind === 'vendor_tool' && text('vendor_security', 'Vendor security certifications and incident reporting obligations')}
        <div className="md:col-span-2">{text('conditions', 'Security conditions that must be satisfied or carried as Contingencies')}</div>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {compliance && (
        <div className="flex gap-2 justify-end">
          <button onClick={() => save('pending')} disabled={!!busy} className={`${BTN} border border-slate-200 text-slate-600`}>{busy === 'pending' ? 'Saving…' : 'Save draft'}</button>
          <button onClick={() => save('complete')} disabled={!!busy} className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-700`}>{busy === 'complete' ? 'Saving…' : 'Mark review complete'}</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
export function TestingTab({ d, ucId, compliance, onChanged }) {
  const records = d.test_records || [];
  const [adding, setAdding] = useState(records.length === 0 && compliance);
  const [f, setF] = useState({ metrics: [{ metric: '', target: '', result: '', met: false }], environment: '', data_used: '', phi_used: false, phi_safeguards: '', vendor_testing: false, vendor_testing_basis: '', outcome: '', variance_control: '', tested_on: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const setMetric = (i, k, v) => setF({ ...f, metrics: f.metrics.map((m, j) => (j === i ? { ...m, [k]: v } : m)) });
  const allMet = f.metrics.filter((m) => m.metric).every((m) => m.met);
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await send(`${API}/api/ai-command/use-cases/${ucId}/test-records`, 'POST', { ...f, outcome: f.outcome || (allMet ? 'met' : 'not_met'), tested_on: f.tested_on || null });
      setAdding(false); onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const OUTCOME = { met: ['Met the metrics', 'bg-emerald-50 text-emerald-700'], variance_controlled: ['Variance controlled', 'bg-amber-50 text-amber-700'], not_met: ['Did not meet', 'bg-rose-50 text-rose-700'] };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500 max-w-2xl">
        Before implementation the tool is tested in an environment comparable to production, on production or representative data, against the performance metrics in the impact assessment. The latest record is the one the Committee decides on. Testing is repeated before a Material Change ships.
      </p>
      {records.length > 0 && (
        <ul className="space-y-3">{records.map((t) => (
          <li key={t.id} className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${OUTCOME[t.outcome]?.[1]}`}>{(OUTCOME[t.outcome]?.[0] || t.outcome).toUpperCase()}</span>
              tested {fmtDate(t.tested_on || t.created_at)} · recorded by {t.recorded_by_label || 'compliance'}{t.vendor_testing ? ' · vendor testing relied on' : ''}{t.phi_used ? ' · PHI used, minimum necessary' : ''}
            </div>
            <table className="mt-2 w-full text-sm">
              <thead><tr className="text-[11px] text-slate-400 uppercase text-left"><th className="py-1 pr-3">Metric</th><th className="py-1 pr-3">Target</th><th className="py-1 pr-3">Result</th><th className="py-1">Met</th></tr></thead>
              <tbody>{(t.metrics || []).map((m, i) => (
                <tr key={i} className="border-t border-slate-100"><td className="py-1 pr-3 text-slate-800">{m.metric}</td><td className="py-1 pr-3 text-slate-600">{m.target}</td><td className="py-1 pr-3 text-slate-600">{m.result}</td><td className={`py-1 font-bold ${m.met ? 'text-emerald-600' : 'text-rose-600'}`}>{m.met ? 'Yes' : 'No'}</td></tr>
              ))}</tbody>
            </table>
            {t.environment && <p className="text-xs text-slate-600 mt-2"><b>Environment:</b> {t.environment}</p>}
            {t.data_used && <p className="text-xs text-slate-600"><b>Data:</b> {t.data_used}</p>}
            {t.vendor_testing_basis && <p className="text-xs text-slate-600"><b>Vendor testing basis:</b> {t.vendor_testing_basis}</p>}
            {t.variance_control && <p className="text-xs text-amber-800 mt-1"><b>Variance control:</b> {t.variance_control}</p>}
          </li>
        ))}</ul>
      )}
      {compliance && !adding && <button onClick={() => setAdding(true)} className={`${BTN} border border-orange-200 bg-orange-50 text-orange-700`}>Record a test run</button>}
      {compliance && adding && (
        <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-white">
          <div className="text-sm font-semibold text-slate-800">New test record</div>
          <div className="space-y-2">
            {f.metrics.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input className={`${INPUT} col-span-4`} placeholder="Metric, e.g. accuracy" value={m.metric} onChange={(e) => setMetric(i, 'metric', e.target.value)} />
                <input className={`${INPUT} col-span-3`} placeholder="Target, e.g. ≥ 95%" value={m.target} onChange={(e) => setMetric(i, 'target', e.target.value)} />
                <input className={`${INPUT} col-span-3`} placeholder="Result" value={m.result} onChange={(e) => setMetric(i, 'result', e.target.value)} />
                <label className="col-span-2 flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={m.met} onChange={(e) => setMetric(i, 'met', e.target.checked)} /> Met</label>
              </div>
            ))}
            <button type="button" onClick={() => setF({ ...f, metrics: [...f.metrics, { metric: '', target: '', result: '', met: false }] })} className="text-xs font-semibold text-orange-700 hover:underline">Add a metric</button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className={LABEL}>Test environment, compared to production</label><input className={INPUT} value={f.environment} onChange={(e) => setF({ ...f, environment: e.target.value })} /></div>
            <div><label className={LABEL}>Data used (production or representative)</label><input className={INPUT} value={f.data_used} onChange={(e) => setF({ ...f, data_used: e.target.value })} /></div>
            <div><label className={LABEL}>Date tested</label><input type="date" className={INPUT} value={f.tested_on} onChange={(e) => setF({ ...f, tested_on: e.target.value })} /></div>
            <div>
              <label className={LABEL}>Outcome</label>
              <select className={INPUT} value={f.outcome || (allMet ? 'met' : 'not_met')} onChange={(e) => setF({ ...f, outcome: e.target.value })}>
                <option value="met" disabled={!allMet}>Met the performance metrics</option>
                <option value="variance_controlled">Did not meet, variance controlled by the mitigation plan</option>
                <option value="not_met">Did not meet</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.phi_used} onChange={(e) => setF({ ...f, phi_used: e.target.checked })} /> PHI was used in testing</label>
            {f.phi_used && <div className="md:col-span-2"><label className={LABEL}>Minimum necessary and safeguards applied</label><input className={INPUT} value={f.phi_safeguards} onChange={(e) => setF({ ...f, phi_safeguards: e.target.value })} /></div>}
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.vendor_testing} onChange={(e) => setF({ ...f, vendor_testing: e.target.checked })} /> Vendor testing relied on</label>
            {f.vendor_testing && <div className="md:col-span-2"><label className={LABEL}>Basis: performed on our data or a like population, in a comparable environment, results reviewed by us</label><textarea className={INPUT} rows={2} value={f.vendor_testing_basis} onChange={(e) => setF({ ...f, vendor_testing_basis: e.target.value })} /></div>}
            {(f.outcome === 'variance_controlled') && <div className="md:col-span-2"><label className={LABEL}>How the mitigation plan controls the variance</label><textarea className={INPUT} rows={2} value={f.variance_control} onChange={(e) => setF({ ...f, variance_control: e.target.value })} /></div>}
          </div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex gap-2 justify-end">
            {records.length > 0 && <button onClick={() => setAdding(false)} className={`${BTN} border border-slate-200 text-slate-600`}>Cancel</button>}
            <button onClick={submit} disabled={busy} className={`${BTN} bg-slate-900 text-white`}>{busy ? 'Recording…' : 'Record test results'}</button>
          </div>
        </div>
      )}
      {!compliance && records.length === 0 && <p className="text-sm text-slate-400">No test results recorded yet.</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
/** Contingencies: each one names what, who, and by when (Sec 7). */
export function ContingencyEditor({ rows, onChange, users = [] }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-start">
          <input className={`${INPUT} col-span-6`} placeholder="What must be done" value={r.item} onChange={(e) => set(i, 'item', e.target.value)} />
          <input className={`${INPUT} col-span-3`} placeholder="Owner" list="ai-contingency-owners" value={r.owner} onChange={(e) => set(i, 'owner', e.target.value)} />
          <input type="date" className={`${INPUT} col-span-2`} value={r.due || ''} onChange={(e) => set(i, 'due', e.target.value)} />
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="col-span-1 text-slate-400 hover:text-rose-600 text-lg leading-none pt-1.5">×</button>
        </div>
      ))}
      <datalist id="ai-contingency-owners">{users.map((u) => <option key={u.id} value={u.name || u.email} />)}</datalist>
      <button type="button" onClick={() => onChange([...rows, { item: '', owner: '', due: '' }])} className="text-xs font-semibold text-orange-700 hover:underline">Add a contingency</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
export function ProgramSettingsCard() {
  const [f, setF] = useState({ monitoring_months_high: '', monitoring_months_medium: '', monitoring_months_low: '', material_change_definition: '' });
  const [saved, setSaved] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/ai-command/settings`, { credentials: 'include' });
    const d = await r.json();
    if (d.success && d.settings) setF({
      monitoring_months_high: d.settings.monitoring_months_high ?? '', monitoring_months_medium: d.settings.monitoring_months_medium ?? '',
      monitoring_months_low: d.settings.monitoring_months_low ?? '', material_change_definition: d.settings.material_change_definition || '',
    });
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setErr(''); setSaved('');
    try { await send(`${API}/api/ai-command/settings`, 'PUT', f); setSaved('Saved.'); }
    catch (e) { setErr(e.message); }
  };
  const unset = !f.monitoring_months_high || !f.monitoring_months_medium || !f.monitoring_months_low;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 md:col-span-2">
      <h3 className="text-sm font-bold text-slate-800 mb-1">Program settings</h3>
      <p className="text-xs text-slate-500 mb-4">What COM028 leaves to the Compliance Officer: the minimum monitoring frequency for each risk rating (Sections 4 and 9), and what counts as a Material Change to an approved tool.</p>
      {unset && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 mb-3">Monitoring frequencies are not set yet. Each approved tool is monitored at least this often.</div>}
      <div className="grid md:grid-cols-3 gap-3 mb-3">
        {[['monitoring_months_high', 'High risk, every (months)'], ['monitoring_months_medium', 'Medium risk, every (months)'], ['monitoring_months_low', 'Low risk, every (months)']].map(([k, l]) => (
          <div key={k}><label className={LABEL}>{l}</label><input type="number" min={1} max={36} className={INPUT} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
        ))}
      </div>
      <label className={LABEL}>Material Change, as this organization defines it</label>
      <textarea className={INPUT} rows={3} value={f.material_change_definition} onChange={(e) => setF({ ...f, material_change_definition: e.target.value })}
        placeholder="A change in the process the tool supports, something added to or removed from what it does, a change in its risk level, a changed screening answer, or a change of vendor or model." />
      <div className="flex items-center gap-3 mt-3">
        <button onClick={save} className={`${BTN} bg-slate-900 text-white`}>Save settings</button>
        {saved && <span className="text-xs text-emerald-700">{saved}</span>}
        {err && <span className="text-xs text-rose-600">{err}</span>}
      </div>
    </div>
  );
}
